// Order → Print shipping label → Custom: the shared label form prefilled from
// the order, everything overridable including the carrier — for when the
// customer's chosen pickup point is dead or a carrier is down. The result
// ATTACHES to the order (tracking, Reprint, Cancel, packing scan gate), which
// is exactly what Tools → Print a label deliberately does not do.
// (Claude, 2026-08-30)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authFetch, API } from './api';
import LabelForm, { LabelFormOpts, LabelFormInitial } from './LabelForm';

// Comment authors get the order panel's color language so a glance says who
// speaks: the Label Doctor (automated diagnosis) purple, the pre-buy sweep
// amber, humans gray.
function noteTone(username: string): string {
  if (username === 'Claude API') return 'border-purple-800/60 bg-purple-950/20 text-purple-200';
  if (username === 'prebuy') return 'border-amber-800/50 bg-amber-950/20 text-amber-200';
  return 'border-gray-800 bg-gray-900/40 text-gray-300';
}

export default function CustomOrderLabelPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState<any | null>(null);
  const [allNotes, setAllNotes] = useState(false);

  useEffect(() => {
    authFetch(`${API}/orders/${orderId}/custom-label/defaults`)
      .then(async r => {
        const d = await r.json();
        if (!r.ok) { setErr(d.detail || 'Could not load the order.'); return; }
        setData(d);
      })
      .catch(() => setErr('Could not load the order.'));
  }, [orderId]);

  if (err) {
    return (
      <div className="p-6 max-w-5xl">
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-3">{err}</div>
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg">← Back</button>
      </div>
    );
  }
  if (!data) return <div className="p-6 text-gray-500 text-sm">Loading…</div>;

  const d = data.defaults || {};
  const orderNo = data.order?.order_number || orderId;
  const activeLabels = data.active_labels || [];

  if (done) {
    return (
      <div className="p-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-white mb-4">Custom label printed</h1>
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5 space-y-2 text-sm">
          <div className="text-white">Order <span className="font-semibold">{orderNo}</span> now carries label
            <span className="font-mono text-blue-400 ml-2">{done.tracking}</span>
            <span className="text-gray-400 ml-2">({(done.carrier || '').replace('_', ' ')})</span>
          </div>
          <div className="text-gray-400">It is attached to the order — Reprint, Cancel and the packing scan gate all use it.</div>
          {done.warning && (
            <div className="text-amber-400 bg-amber-900/20 border border-amber-800/40 rounded-lg px-3 py-2 mt-2">⚠ {done.warning}</div>
          )}
        </div>
        <button onClick={() => navigate(-1)}
          className="mt-4 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
          ← Back to orders
        </button>
      </div>
    );
  }

  const opts: LabelFormOpts = {
    senders: data.senders || [],
    carriers: data.carriers || [],
    default_weight_kg: data.default_weight_kg ?? 0.5,
    packeta_services: data.packeta_services || [],
  };
  const initial: LabelFormInitial = {
    senderId: d.sender_id ?? null,
    carrier: d.carrier ?? '',
    dtype: d.delivery_type || 'hd',
    rcv: d.receiver || {},
    reference: d.reference || '',
    weightKg: d.weight_kg,
    pp: d.pickup_point || null,
    packetaMethod: d.packeta_method_name || '',
    // Cash on delivery is editable here and nowhere else — this page is the only
    // one with a real order (and therefore a payment kind) behind it.
    paymentKind: d.payment_kind ?? null,
    codAmount: d.cod_amount ?? null,
    currency: d.currency ?? null,
    fieldHints: d.field_hints ?? null,
  };

  return (
    <div className="p-6 max-w-[1500px]">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Custom label for order {orderNo}</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            {data.order?.store_name ? `${data.order.store_name} · ` : ''}
            Prefilled from the order — change anything, the carrier included. The label attaches to the order:
            tracking, Reprint, Cancel and the packing gate all use it.
          </p>
        </div>
        <button onClick={() => navigate(-1)} className="flex-none px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg">← Back</button>
      </div>

      {/* Form on the left; the order's notes ride the otherwise-empty right
          column, sticky, so the Label Doctor's diagnosis stays in view while
          the person scrolls to the exact field it names. */}
      <div className="flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-0 md:min-w-[560px] max-w-4xl">
          <div className="space-y-3 mb-5">
            {activeLabels.length > 0 && (
              <div className="text-amber-400 text-sm bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-2.5">
                ⚠ This order already has {activeLabels.length === 1 ? 'an active label' : `${activeLabels.length} active labels`}
                {' '}({activeLabels.map((l: any) => l.tracking_number).join(', ')}). Printing here buys a NEW shipment —
                to print the same label again, use Reprint on the order instead.
              </div>
            )}
            {d.payment_kind === 'cod' && (
              <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/40 rounded-lg px-4 py-2.5">
                💰 Cash-on-delivery order — only Packeta transmits COD. Any other carrier will be refused.
              </div>
            )}
            {data.config_error && (
              <div className="text-amber-400 text-sm bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-2.5">
                {data.config_error}
              </div>
            )}
            {data.label_stale && (
              <div className="text-amber-400 text-sm bg-amber-900/20 border border-amber-800/40 rounded-lg px-4 py-2.5">
                ⚠ The shop edited this order after a label was bought — check the parcel contents
                against the current order before printing.
              </div>
            )}
          </div>

          <LabelForm
            opts={opts}
            initial={initial}
            submitLabel="🖨 Print custom label"
            onSubmit={body => authFetch(`${API}/orders/${orderId}/custom-label`, {
              method: 'POST', body: JSON.stringify(body),
            })}
            onDone={res => setDone(res)}
          />
        </div>

        {(data.notes || []).length > 0 && (
          <aside className="w-full md:w-96 max-w-full flex-none md:sticky top-6 md:max-h-[calc(100vh-3rem)] overflow-y-auto
                            bg-[#1a1d27] rounded-xl border border-gray-800 px-4 py-3">
            <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2">
              Notes on this order
            </div>
            <div className="space-y-2">
              {(allNotes ? data.notes : data.notes.slice(0, 6)).map((n: any, i: number) => (
                <div key={i} className={`border rounded-lg px-3 py-2 text-sm ${noteTone(n.username)}`}>
                  {/* break-words: carrier errors carry unbroken URL/JSON runs
                      that otherwise punch through the box sideways */}
                  <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{n.comment}</div>
                  <div className="text-xs opacity-60 mt-1">
                    {n.username} · {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  </div>
                </div>
              ))}
            </div>
            {data.notes.length > 6 && (
              <button onClick={() => setAllNotes(v => !v)}
                className="mt-2 text-xs text-blue-400 hover:text-blue-300">
                {allNotes ? 'Show fewer' : `Show all ${data.notes.length} notes`}
              </button>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
